import { Component } from '@theme/component';

/**
 * <caldera-video>
 *
 * Wraps a Caldera video (one <video>, or one mobile + one desktop variant)
 * and its two-button overlay (play/pause + mute/unmute) rendered by
 * snippets/caldera-video-controls.liquid.
 *
 * Mirrors the live calderalab.com pattern: poster up front, raw <video>
 * with `preload="metadata"`, click-to-play, mute toggle. Buttons reflect
 * native `play` / `pause` / `volumechange` events, so anything else that
 * drives playback (e.g. <caldera-step-media> tab activation calling
 * .play() directly) is reflected in the button state automatically.
 *
 * @typedef {object} CalderaVideoRefs
 * @property {HTMLVideoElement[]} [video] - One <video> per uploaded variant (mobile, desktop). Array via `ref="video[]"`.
 * @property {HTMLButtonElement} [playButton] - Play / pause toggle.
 * @property {HTMLButtonElement} [soundButton] - Mute / unmute toggle.
 *
 * @extends {Component<CalderaVideoRefs>}
 */
class CalderaVideo extends Component {
  #abortController = new AbortController();
  #preloadStarted = false;
  /** @type {IntersectionObserver | null} */
  #viewportObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // An AbortController is single-use: after a DOM move (theme editor
    // reorder, Intelligems section-reorder variant) disconnectedCallback has
    // already aborted this one, and addEventListener with an aborted signal
    // is a silent no-op — the buttons would stop reflecting player state.
    // Recreate it on every connect so listeners rebind.
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    for (const video of this.#videos) {
      video.addEventListener('play', this.#onPlay, { signal });
      video.addEventListener('pause', this.#onPause, { signal });
      video.addEventListener('volumechange', this.#onVolumeChange, { signal });
    }

    // Warm the video buffer as soon as the player scrolls into view so the
    // user gets near-instant playback when they press play. IntersectionObserver
    // is the primary trigger (fires seconds before any click), pointerenter +
    // focusin are belt-and-braces for fast clicks immediately on load.
    this.#viewportObserver = new IntersectionObserver(this.#onIntersect, {
      rootMargin: '200px',
    });
    this.#viewportObserver.observe(this);
    this.addEventListener('pointerenter', this.#warmBuffer, { signal, once: true });
    this.addEventListener('focusin', this.#warmBuffer, { signal, once: true });

    // Sync initial state — covers the case where the video was already
    // playing or unmuted (e.g. on hot reload, or when <caldera-step-media>
    // calls .play() before this component connects).
    this.#syncPlayState();
    this.#syncMuteState();

    // Autoplay videos: kick off playback explicitly as a belt-and-braces on
    // top of the native `autoplay` attribute (muted + playsinline satisfy the
    // browser autoplay policy). #warmBuffer leaves these alone so its load()
    // can't interrupt an in-progress autoplay.
    for (const video of this.#videos) {
      if (!video.autoplay) continue;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#viewportObserver?.disconnect();
    this.#viewportObserver = null;
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.#warmBuffer();
        this.#viewportObserver?.disconnect();
        this.#viewportObserver = null;
        break;
      }
    }
  };

  #warmBuffer = () => {
    if (this.#preloadStarted) return;
    this.#preloadStarted = true;
    for (const video of this.#videos) {
      video.preload = 'auto';
      // An autoplay video is already fetching/playing natively — calling
      // load() would reset it mid-start and often strands it on the poster.
      if (video.autoplay) continue;
      // Force the browser to start fetching data immediately rather than
      // waiting for its own heuristics to decide.
      video.load();
    }
  };

  /** @returns {HTMLVideoElement[]} */
  get #videos() {
    return this.refs.video ?? [];
  }

  /**
   * The currently visible <video> element (used when mobile + desktop
   * variants are both rendered — only one is shown by CSS at a time).
   * Falls back to the first video if none has an offsetParent yet (e.g.
   * inside a hidden tab panel).
   *
   * @returns {HTMLVideoElement | null}
   */
  get #visibleVideo() {
    const videos = this.#videos;
    if (videos.length === 0) return null;
    return videos.find((video) => video.offsetParent !== null) || videos[0] || null;
  }

  handleTogglePlay = () => {
    const video = this.#visibleVideo;
    if (!video) return;
    if (video.paused) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    } else {
      video.pause();
    }
  };

  handleToggleSound = () => {
    const video = this.#visibleVideo;
    if (!video) return;
    // Apply to every <video> in this component so the mute state survives
    // a viewport-driven swap between the mobile and desktop variants.
    const nextMuted = !video.muted;
    for (const v of this.#videos) {
      v.muted = nextMuted;
    }
  };

  #onPlay = () => this.#syncPlayState();
  #onPause = () => this.#syncPlayState();
  #onVolumeChange = () => this.#syncMuteState();

  #syncPlayState() {
    const button = this.refs.playButton;
    if (!button) return;
    const video = this.#visibleVideo;
    const playing = !!video && !video.paused;
    button.dataset.playbackStatus = playing ? 'playing' : 'paused';
    const label = playing ? button.dataset.labelPause : button.dataset.labelPlay;
    if (label) button.setAttribute('aria-label', label);
  }

  #syncMuteState() {
    const button = this.refs.soundButton;
    if (!button) return;
    const video = this.#visibleVideo;
    const muted = !video || video.muted;
    button.dataset.muteStatus = muted ? 'muted' : 'unmuted';
    const label = muted ? button.dataset.labelUnmute : button.dataset.labelMute;
    if (label) button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('caldera-video')) {
  customElements.define('caldera-video', CalderaVideo);
}
